// this is directly copied from TAILWIND so if SIZING would
import * as _ from 'lodash';
import { EventType } from './event-emitter';
import { GoalSubscriptionTab } from './models/goalSubscriptions';
import { LegalDocumentType } from './models/legalDocuments';
import { VersionedEntityType } from './models/versions';

const DEFAULT_LANGUAGE = 'pl';
const DEFAULT_CURRENCY = 'PLN';

// change this must be updated as well
const DESKTOP_MAX_WIDTH = 1536;

const UPLOAD_IMAGE_MAX_SIZE = 20 * 1024 * 1024; // 20MB
const UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
const UPLOAD_VIDEO_MAX_SIZE = 1024 * 1024 * 1024; // 1GB
const UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES = ['video/mp4', 'video/ogg', 'video/webm'];
const UPLOAD_AUDIO_MAX_SIZE = 100 * 1024 * 1024; // 100MB
const UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES = ['audio/mpeg', 'audio/ogg', 'audio/wav'];
const UPLOAD_DOCUMENT_ACCEPT_CONTENT_TYPES = [
  'application/javascript',
  'application/json',
  'application/msword',
  'application/pdf',
  'application/rtf',
  'application/sql',
  'application/typescript',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/x-msdownload',
  'application/x-httpd-php',
  'application/x-ipynb+json',
  'application/x-powershell',
  'application/x-yaml',
  'application/yaml',
  'text/css',
  'text/csv',
  'text/html',
  'text/java',
  'text/javascript',
  'text/markdown',
  'text/plain',
  'text/python',
  'text/ruby',
  'text/sass',
  'text/scss',
  'text/tab-separated-values',
  'text/typescript',
  'text/x-c',
  'text/x-cpp',
  'text/x-csharp',
  'text/x-go',
  'text/x-python-script',
  'text/x-python',
  'text/x-rust',
  'text/x-shellscript',
  'text/x-yaml',
  'text/xml',
  'text/yaml'
];
const UPLOAD_ARCHIVE_ACCEPT_CONTENT_TYPES = [
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-powerbi',
  'application/vnd.sqlite3',
  'application/vnd.apache.parquet',
  'application/x-figma'
];
// MIME type to extension for improver attachments
const IMPROVER_ATTACHMENT_MIME_TYPE_TO_EXTENSION = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'application/x-ipynb+json': 'IPYNB',
  'text/x-python': 'PY',
  'text/x-python-script': 'PY',
  'application/vnd.sqlite3': 'SQLITE',
  'application/x-sqlite3': 'SQLITE',
  'application/vnd.apache.parquet': 'PARQUET',
  'application/x-figma': 'FIG',
  'text/tab-separated-values': 'TSV'
};

const ALLOWED_FILE_UPLOAD_CONTENT_TYPED = {
  PDF: 'application/pdf',
  PNG: 'image/png',
  JPG: 'image/jpeg',
  JSON: 'application/json',
  IPYNB: 'application/x-ipynb+json',
  CSV: 'text/csv',
  PY: 'text/x-python',
  SQLITE_DB: 'application/vnd.sqlite3',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PARQUET: 'application/vnd.apache.parquet',
  FIG: 'application/x-figma',
  TSV: 'text/tab-separated-values'
};

const UPLOAD_MEDIAITEM_MAX_SIZE = 1.5 * 1024 * 1024 * 1024; // 1.5GB
const UPLOAD_MEDIAITEM_ACCEPT_CONTENT_TYPES = [
  ...UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_DOCUMENT_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_ARCHIVE_ACCEPT_CONTENT_TYPES
];

const UPLOAD_MEDIA_ONLY_ACCEPT_CONTENT_TYPES = [
  ...UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES,
  ...UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES
];
const MAX_ATTACHMENT_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const CONVERSATION_MESSAGES_DEFAULT_SIZE = 500; // Default number of messages fetched per request
const CONVERSATION_MESSAGES_FETCH_BATCH_SIZE = 15;
const CONVERSATION_MESSAGES_FIRST_INDEX_BASE = 10000;
const CONVERSATION_MESSAGES_INCREASE_VIEWPORT_BY_TOP = 2000;
const CONVERSATION_MESSAGES_INCREASE_VIEWPORT_BY_BOTTOM = 5000; // only for initial load
const CONVERSATION_MESSAGE_QUEUE_IDLE_MS = 5000;
const MENTORBOT_THINKING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes safety timeout if bot never replies
// The reply lock only starts when Pusher says generation began, a moment after the POST answers.
// This bridges that gap. It stays short: a mentor on the chat means no event ever arrives.
const CONVERSATION_CHATBOT_ACK_TIMEOUT_MS = 10 * 1000;
// One mentorbot turn patches several messages, so a burst of MESSAGE_UPDATED must cost one ledger read.
const IMPRV_COINS_BALANCE_REFRESH_DEBOUNCE_MS = 1500;
// No single event lands after every charge. A recommendation bills the user but creates no
// message, so the backend skips MESSAGE_UPDATED for it and the accepted reply arrives as a new
// message instead. Listening to all three is cheap because the debounce collapses the burst.
const CONVERSATION_CHARGE_EVENTS = [
  EventType.MESSAGE_UPDATED,
  EventType.NEW_MESSAGE_RECEIVED,
  EventType.CHATBOT_REPLY_FINISHED
];
const BLOCK_EVALUATION_CHARGE_EVENTS = [EventType.BLOCK_OUTPUT_AI_USAGE_RECORDED];
const IMPRV_COINS_LOW_BALANCE_THRESHOLD = 100;
const IMPRV_COINS_EXPIRY_WARNING_DAYS = 30;
const BLOCK_OUTPUT_FLUSH_INTERVAL_MS = 60 * 1000; // 60 seconds
const BLOCK_OUTPUT_FLUSH_THRESHOLD = 100; // 100 block outputs
const VIDEO_HEARTBEAT_INTERVAL_MS = 5; // 5 seconds

// Reorderable list (drag-and-drop) sizing
const REORDERABLE_LIST_COLLAPSED_SLOT_HEIGHT_PX = 8;
const REORDERABLE_LIST_FALLBACK_DRAG_ROW_HEIGHT_PX = 80;
const REORDERABLE_LIST_FALLBACK_DRAG_ROW_WIDTH_PX = 200;

const BREAKPOINTS = {
  SM: 640, // px
  MOBILE: 640, // px | ALIAS FOR MOBILE
  MD: 768, // px
  LG: 1024, // px
  XL: 1280, // px
  '2XL': 1586 // px
};

const SIZING = {
  none: 0,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 28,
  xxxl: 32,
  huge: 48,
  xxs: 4,
  xs: 8
};

enum AccountsMeFieldsUsage {
  CERTIFICATE = 'CERTIFICATE'
}

/**
 * LOCAL STORAGE KEYS
 */
const LocalStorageKeys = {
  LAST_SCHEDULE_QUEUE_DATE: 'lastScheduleQueueDate',
  INTRO_IS_DONE: 'introIsDone',
  VIDEO_PLAYBACK_SPEED: 'videoPlaybackSpeed',
  COLLAPSED_SECTIONS: 'collapsedSections',
  LAST_NODE_ENTITY_ID: 'lastNodeEntityId',
  GOAL_RECOMMENDATION_BANNER_HIDDEN_UNTIL: 'goalRecommendationCountBannerHiddenUntil',
  GOAL_RECOMMENDATION_COUNT_BADGE_HIDDEN_UNTIL: 'goalRecommendationCountBadgeHiddenUntil',
  PENDING_BLOCK_OUTPUTS: 'pendingBlockOutputs',
  BACKGROUND_ACTIVITIES: 'backgroundActivities',
  LAST_EDITED_NODE_ENTITY_ID: 'lastEditedNodeEntityId',
  TRANSCRIPT_EXPECTATIONS: 'transcriptExpectations'
};

const SessionStorageKeys = {
  ASSIGNMENT_TASK_DRAFTS: 'assignmentTaskDrafts',
  IMPRV_COINS_PURCHASE: 'imprvCoinsPurchase',
  IMPRV_COINS_WARNING_DISMISSED: 'imprvCoinsWarningDismissed',
  LAST_NAVIGATION: 'lastNavigation'
} as const;

const TRANSCRIPT_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * UI Paths
 */
const ROUTER_NAME = {
  HOME: '/',
  EXPLORE: '/explore',
  INTRO: '/intro',
  GOAL_SUBSCRIPTIONS: '/subscriptions',
  GOAL_SUBSCRIPTION: '/subscriptions/[id]',
  CONVERSATIONS: '/conversations',
  CONVERSATION: '/conversations/[id]',
  MESSAGES: '/messages',
  GOAL: '/goals/[id]',
  GOAL_THANK_YOU: '/goals/[id]/thankyou',
  GOAL_EDITOR: '/goals/[id]/editor',
  GOAL_BASIC_EDITOR: '/goals/[id]/basiceditor',
  GOAL_LANDING_PAGE_EDITOR: '/goals/[id]/landingpage/editor',
  GOAL_USERS: '/goals/[id]/users',
  GOAL_USER: '/goals/[id]/users/[userId]',
  GOAL_PACKAGE: '/goal_package/[id]',
  GARMIN_CONNECTIONS: '/garmin/connections',
  GARMIN_CONNECTION_AUTHORIZE: '/garmin/connections/authorize',
  ACCOUNT_ME: '/accounts/me',
  NOTIFICATIONS: '/notifications',
  TASKS: '/tasks',
  TASK: '/tasks/[entityId]',
  ASSIGNMENT: '/assignments/[id]',
  ASSIGNMENTS: '/assignments',
  GOALS: '/goals',
  NEW_STRATEGY_EDITOR: '/strategies/new_strategy/[id]/editor',
  SKILL_GRAPH_EDITOR: '/skill_graphs/[id]',
  TASK_EDITOR: '/tasks/[entityId]/editor',
  VERIFY_EMAIL: '/verify-email',
  PAYMENT_LINK_THANK_YOU: '/payments/link-thankyou',
  PAYMENT_THANK_YOU: '/payments/thankyou',
  APPROVALS: '/approvals',
  NOT_FOUND: '/404',
  COMBO_GRAPH: '/goals/[id]/combo_graph',
  GIVE_ME_MORE: '/give-me-more',
  GOAL_SUBSCRIPTION_VERIFICATION: '/verify/[id]',

  // public legal pages (no auth; content is Polish-only for now, routes are English)
  TERMS: '/terms',
  PRIVACY_POLICY: '/privacy-policy',
  DATA_PROTECTION_OFFICER: '/data-protection-officer',
  RETURN_POLICY: '/return-policy',
  CONTACT: '/contact',

  // dev pages
  DEV_ICONS_SINK: '/dev/icons_sink',
  DEV_INFO: '/dev/info'
};

const AUTH_ERROR_TYPE = {
  ACCESS_DENIED: 'access_denied'
};

const AUTH_ERROR_MESSAGE = {
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED'
};

enum AuthSessionDeathErrorCode {
  BAD_CREDENTIALS = 'BAD_CREDENTIALS',
  EXPIRED_CREDENTIALS = 'EXPIRED_CREDENTIALS',
  REQUIRES_AUTHENTICATION = 'REQUIRES_AUTHENTICATION'
}

const AUTH_SESSION_DEATH_ERROR_CODES: ReadonlySet<string> = new Set(
  Object.values(AuthSessionDeathErrorCode)
);

enum ConversationErrorCode {
  INVALID_INBOX_CURSOR = 'CONVERSATION__INVALID_INBOX_CURSOR'
}

enum PaymentsErrorCode {
  UNKNOWN_IMPRV_COINS_PACKAGE = 'PAYMENTS__UNKNOWN_IMPRV_COINS_PACKAGE'
}

/**
 * Navigation Context - unified context for tracking user navigation
 */
const NAVIGATION_CONTEXT = {
  // Assignment contexts
  INBOX: 'inbox',
  SUBSCRIPTION: 'subscription',
  TASK: 'task',
  UNKNOWN: 'unknown'
};

/**
 * Task Entry Source — tracks HOW a user arrived at a task/block.
 */
const TASK_ENTRY_SOURCE = {
  TOC__SEARCH: 'TOC__SEARCH',
  TOC: 'TOC',
  CHATBOT__MESSAGE: 'CHATBOT_MESSAGE'
};

const MENTOR_USER_IDS_WITH_DISABLED_CHAT =
  process.env.NEXT_PUBLIC_MENTOR_USER_IDS_WITH_DISABLED_CHAT?.split(',')
    .map((id) => id.trim())
    .filter((id) => id)
    .map((id) => parseInt(id)) || [];
const FORCE_ALLOW_CHAT_FOR_GOAL_ENTITY_IDS =
  process.env.NEXT_PUBLIC_FORCE_ALLOW_CHAT_FOR_GOAL_ENTITY_IDS?.split(',')
    .map((id) => id.trim())
    .filter((id) => id) || [];

const API_ENDPOINTS = {
  // Node-based assignment endpoints
  assignmentNodeEvaluateBlockInput: (nodeEntityId: string, goalSubscriptionId: number): string => {
    return `/assignments/nodes/${nodeEntityId}/evaluate_block_input?goalSubscriptionId=${goalSubscriptionId}`;
  },

  assignmentNodeBlockOutputs: (nodeEntityId: string, goalSubscriptionId: number): string => {
    return `/assignments/nodes/${nodeEntityId}/block_outputs?goalSubscriptionId=${goalSubscriptionId}`;
  },

  assignmentNodeBlockOutputResultsAsMentor: (nodeEntityId: string): string => {
    return `/assignments/nodes/${nodeEntityId}/block_output_results/as_mentor`;
  },

  assignmentNodeBlockOutputsAsMentor: (
    nodeEntityId: string,
    goalSubscriptionId: number
  ): string => {
    return `/assignments/nodes/${nodeEntityId}/block_outputs/as_mentor?goalSubscriptionId=${goalSubscriptionId}`;
  },

  // General assignment endpoints
  assignments: (): string => {
    return `/assignments`;
  },

  assignmentById: (assignmentId: string) => {
    return `/assignments/${assignmentId}`;
  },

  assignmentByNode: (nodeEntityId: string): string => {
    return `/assignments/nodes/${nodeEntityId}`;
  },

  // PROGRESS
  progressByTaskEntityId: (taskEntityId: string): string => {
    return `/progress/tasks/${taskEntityId}`;
  },
  progressByGoalEntityId: (goalEntityId: string): string => {
    return `/progress/goals/${goalEntityId}`;
  },
  progressBlocksByTaskEntityId: (taskEntityId: string): string => {
    return `/progress/tasks/${taskEntityId}/blocks`;
  },

  // SEARCH
  search: (): string => {
    return `/search`;
  },

  // ACCOUNTS
  accountsMe: (): string => {
    return `/accounts/me`;
  },
  accountsProfile: (id: string): string => {
    return `/accounts/${id}/profile`;
  },
  accountsMarkIntroAsDone: (): string => {
    return `/accounts/me/mark_intro_as_done`;
  },
  accountsApprovals: (): string => {
    return `/accounts/me/approvals`;
  },
  accountsReferral: (): string => {
    return `/accounts/me/referral`;
  },
  accountsEarnings: (): string => {
    return `/accounts/earnings`;
  },
  accountsMentorEarnings: (): string => {
    return `/accounts/mentor-earnings`;
  },
  accountsOrders: (): string => {
    return `/accounts/orders`;
  },
  // LEGAL DOCUMENTS
  legalDocuments: (types: LegalDocumentType[]): string => {
    return `/legal_documents`;
  },

  // GOALS
  getComboGoalGraph: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/combo_graph`;
  },
  getGoals: (): string => {
    return `/goals`;
  },
  getGoal: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}`;
  },
  getGoalTOC: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/toc`;
  },
  getGoalsRecommendationsCount: (): string => {
    return `/goals/recommendations/count`;
  },
  getGoalPrices: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/prices`;
  },
  createGoalPrice: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/prices`;
  },
  getGoalPrice: (goalEntityId: string, goalPriceEntityId: string): string => {
    return `/goals/${goalEntityId}/prices/${goalPriceEntityId}`;
  },
  updateGoalPrice: (goalEntityId: string, goalPriceEntityId: string): string => {
    return `/goals/${goalEntityId}/prices/${goalPriceEntityId}`;
  },
  updateGoal: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}`;
  },
  goalVisibilityVector: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/visibility_vector`;
  },
  goalLandingPage: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/landing_page`;
  },
  publishGoalPrice: (goalEntityId: string, goalPriceEntityId: string): string => {
    return `/goals/${goalEntityId}/prices/${goalPriceEntityId}/publish`;
  },
  calculateGoalPriceDetails: (): string => {
    return `/goals/calculate_pricing_details`;
  },
  goalEarnings: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/earnings`;
  },

  // GOAL PACKAGES
  getGoalPackage: (packageId: string): string => {
    return `/goal_packages/${packageId}`;
  },

  // GOAL SUBSCRIPTIONS
  goalSubscriptions: (): string => {
    return `/goal_subscriptions`;
  },
  goalSubscriptionsAsMentor: (): string => '/goal_subscriptions/as_mentor',
  goalSubscription: (goalSubscriptionId: number): string => {
    return `/goal_subscriptions/${goalSubscriptionId}`;
  },
  goalSubscriptionVerification: (verificationId: string): string => {
    return `/goal_subscriptions/verify/${verificationId}`;
  },
  goalSubscriptionsSummary: (): string => {
    return `/goal_subscriptions/summary`;
  },
  updateGoalSubscription: (goalSubscriptionId: number): string => {
    return `/goal_subscriptions/${goalSubscriptionId}`;
  },

  // ORDERS
  orders: (): string => {
    return `/orders`;
  },
  imprvCoinsLedger: (): string => {
    return `/orders/imprv-coins/ledger`;
  },
  orderInvoice: (orderId: string): string => {
    return `/orders/${orderId}/invoice`;
  },
  orderInvoicePdf: (orderId: string): string => {
    return `/orders/${orderId}/invoice/pdf`;
  },
  orderGrants: (): string => {
    return `/orders/grants`;
  },

  // CONVERSATIONS
  conversations_summaries_inbox: (): string => {
    return `/conversations/inbox`;
  },
  conversations_summaries_inbox_clean: (conversationId: number): string => {
    return `/conversations/inbox/${conversationId}/clean`;
  },
  conversations_summaries_inbox_unclean: (conversationId: number): string => {
    return `/conversations/inbox/${conversationId}/unclean`;
  },

  // LIKES & UNLIKES
  likesLike: (): string => {
    return `/likes/like`;
  },
  likesUnlike: (): string => {
    return `/likes/unlike`;
  },

  // MARKETING
  notificationIntents: (): string => {
    return `/marketing/notification_intents`;
  },

  // PAYMENTS
  paymentsCreateSession: (): string => {
    return `/payments/sessions`;
  },
  paymentsImprvCoinsPackages: (): string => {
    return `/payments/imprv-coins/packages`;
  },

  // SKILL GRAPHS
  skillGraphs: (): string => `/skill_graphs`,
  skillGraph: (skillGraphEntityId: string): string => `/skill_graphs/${skillGraphEntityId}`,
  skillGraphPublish: (skillGraphEntityId: string): string =>
    `/skill_graphs/${skillGraphEntityId}/publish`,
  skillGraphNodes: (skillGraphEntityId: string): string =>
    `/skill_graphs/${skillGraphEntityId}/nodes`,
  skillGraphNode: (skillGraphEntityId: string, nodeEntityId: string): string =>
    `/skill_graphs/${skillGraphEntityId}/nodes/${nodeEntityId}`,
  skillGraphEdges: (skillGraphEntityId: string): string =>
    `/skill_graphs/${skillGraphEntityId}/edges`,
  skillGraphEdge: (skillGraphEntityId: string, edgeEntityId: string): string =>
    `/skill_graphs/${skillGraphEntityId}/edges/${edgeEntityId}`,

  // MEDIAITEMS
  mediaitems: (): string => `/mediaitems`,
  mediaitemById: (id: number): string => `/mediaitems/${id}`,
  signedUploads: (): string => `/mediaitems/signed_uploads`
};

const UIPaths = {
  goalSubscriptionVerification: (verifyId: string): string => {
    return `/verify/${verifyId}`;
  },

  home: (): string => {
    return '/';
  },

  explore: (): string => {
    return '/explore';
  },

  messages: (): string => {
    return '/messages';
  },

  goal: (goalEntityId: string, maxVersionType?: VersionedEntityType, fragment?: string): string => {
    let url = `/goals/${goalEntityId}`;
    if (maxVersionType) {
      url += `?maxVersionType=${maxVersionType}`;
    }
    if (fragment) {
      url += `#${fragment}`;
    }
    return url;
  },

  goalGraph: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/combo_graph`;
  },

  goalPackage: (packageId: string): string => {
    return `/goal_package/${packageId}`;
  },

  terms: (): string => {
    return '/terms';
  },

  privacyPolicy: (): string => {
    return '/privacy-policy';
  },

  dataProtectionOfficer: (): string => {
    return '/data-protection-officer';
  },

  returnPolicy: (): string => {
    return '/return-policy';
  },

  contact: (): string => {
    return '/contact';
  },

  shareableGoal: (goalEntityId: string, referralCode?: string): string => {
    const shareLink = `${process.env.NEXT_PUBLIC_API_BASE_URL}/goals/${goalEntityId}/share`;
    return referralCode ? `${shareLink}?ref=${encodeURIComponent(referralCode)}` : shareLink;
  },

  shareableAssignment: (
    goalEntityId: string,
    nodeEntityId: string,
    blockEntityId?: string,
    currentTimeSeconds?: number
  ): string => {
    const url = new URL(`${process.env.NEXT_PUBLIC_SHARE_BASE_URL}/assignments`);

    url.searchParams.set('goalEntityId', goalEntityId);
    url.searchParams.set('nodeEntityId', nodeEntityId);

    if (blockEntityId) {
      url.searchParams.set('blockEntityId', blockEntityId);
    }
    if (currentTimeSeconds) {
      url.searchParams.set('t', currentTimeSeconds.toString());
    }

    return url.toString();
  },

  task: (
    taskEntityId: string,
    taskAssignmentId?: number,
    maxVersionType?: VersionedEntityType
  ): string => {
    let q = '';
    if (taskAssignmentId) {
      q = `taskAssignmentId=${taskAssignmentId}`;
    }
    if (maxVersionType) {
      q = q ? `${q}&maxVersionType=${maxVersionType}` : `maxVersionType=${maxVersionType}`;
    }

    return `/tasks/${taskEntityId}?${q}`;
  },

  goalSubscriptions: (): string => {
    return '/subscriptions';
  },

  goalSubscription: (
    goalSubscriptionId: number,
    messageId?: number,
    tab?: GoalSubscriptionTab
  ): string => {
    const baseUrl = `/subscriptions/${goalSubscriptionId}`;
    const query = new URLSearchParams();
    if (messageId) {
      query.set('messageId', messageId.toString());
    }

    if (tab) {
      query.set('tab', tab);
    }

    if (query.toString() !== '') {
      return `${baseUrl}?${query.toString()}`;
    }

    return baseUrl;
  },

  masterConversation: (): string => {
    return `/conversations/master`;
  },

  goals: (): string => {
    return '/goals';
  },

  goalEditor: (goalEntityId: string, tab?: GoalManagementTab): string => {
    const base = `/goals/${goalEntityId}/editor`;
    return tab ? `${base}?tab=${tab}` : base;
  },

  goalBasicEditor: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/basiceditor`;
  },

  goalLandingPageEditor: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/landingpage/editor`;
  },

  goalUsers: (goalEntityId: string): string => {
    return `/goals/${goalEntityId}/users`;
  },

  goalUser: (
    goalEntityId: string,
    userId: number,
    goalSubscriptionId?: number,
    tab?: string,
    returnTo?: string
  ): string => {
    let url = `/goals/${goalEntityId}/users/${userId}`;
    const params = new URLSearchParams();

    if (goalSubscriptionId) {
      params.set('goalSubscriptionId', goalSubscriptionId.toString());
    }

    if (tab) {
      params.set('tab', tab);
    }

    if (returnTo) {
      params.set('returnTo', returnTo);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    return url;
  },

  chatbotEditor: (chatbotEntityId: string): string => {
    return `/chatbots/${chatbotEntityId}/editor`;
  },

  newStrategyEditor: (strategyEntityId: number, goalEntityId?: string): string => {
    const base = `/strategies/new_strategy/${strategyEntityId}/editor`;
    return goalEntityId ? `${base}?goalEntityId=${goalEntityId}` : base;
  },

  tasks: (): string => {
    return '/tasks';
  },

  taskEditor: (taskEntityId: string): string => {
    return `/tasks/${taskEntityId}/editor`;
  },

  skillGraphEditor: (skillGraphEntityId: string): string => {
    return `/skill_graphs/${skillGraphEntityId}`;
  },

  assignmentByNode: (nodeEntityId: string, goalSubscriptionId: number): string => {
    return `/assignments?nodeEntityId=${nodeEntityId}&goalSubscriptionId=${goalSubscriptionId}`;
  },

  assignments: (
    goalEntityId?: string,
    goalSubscriptionId?: number,
    nodeEntityId?: string,
    blockEntityId?: string,
    currentTimeSeconds?: number,
    tab?: string,
    goToNewContent?: boolean
  ): string => {
    let url = '/assignments';
    const params = new URLSearchParams();

    if (goalEntityId) {
      params.set('goalEntityId', goalEntityId);
    }

    if (goalSubscriptionId) {
      params.set('goalSubscriptionId', goalSubscriptionId.toString());
    }

    if (nodeEntityId) {
      params.set('nodeEntityId', nodeEntityId);
    }
    if (blockEntityId) {
      params.set('blockEntityId', blockEntityId);
    }
    if (!_.isNil(currentTimeSeconds)) {
      params.set('t', currentTimeSeconds.toString());
    }

    if (tab) {
      params.set('tab', tab);
    }

    if (goToNewContent) {
      params.set('goToNewContent', '1');
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    return url;
  },

  accountProfile: (userId: number): string => {
    return `/accounts/${userId}/profile`;
  },

  accountMasterProfile: (): string => {
    return `/accounts/master/profile`;
  },

  accountMe: (usage?: AccountsMeFieldsUsage, returnTo?: string): string => {
    const params = new URLSearchParams();
    if (usage) params.set('usage', usage);
    if (returnTo) params.set('returnTo', returnTo);
    const query = params.toString();
    return query ? `/accounts/me?${query}` : '/accounts/me';
  },

  intro: (): string => {
    return '/intro';
  },

  garminConnections: (): string => {
    return '/garmin/connections';
  },

  notifications: (): string => {
    return '/notifications';
  },

  goalSubscriptionPending: (goalSubscriptionId: number): string => {
    return `/subscriptions/${goalSubscriptionId}/pending`;
  },

  goalSubscriptionCancel: (goalSubscriptionId: number): string => {
    return `/subscriptions/${goalSubscriptionId}/cancel`;
  },

  paymentsThankYou: (goalEntityId: string): string => {
    return `/payments/thankyou?product__data__goal_entity_id=${goalEntityId}`;
  },

  giveMeMore: (): string => {
    return '/give-me-more';
  },

  approvals: (returnTo?: string): string => {
    if (returnTo) {
      return `/approvals?returnTo=${encodeURIComponent(returnTo)}`;
    }
    return '/approvals';
  }
};

enum Page {
  EXPLORE = 'EXPLORE',
  GOAL_SUBSCRIPTION = 'GOAL_SUBSCRIPTION',
  GOAL_SUBSCRIPTIONS = 'GOAL_SUBSCRIPTIONS',
  CONVERSATION = 'CONVERSATION'
}

enum GoalUserTabs {
  STATS = 'STATS',
  MESSAGES = 'MESSAGES'
}

enum AssignmentsTabs {
  TOC = 'TOC',
  MESSAGES = 'MESSAGES',
  SKILL_GRAPH = 'SKILL_GRAPH'
}

enum GoalManagementTab {
  CONTENT = 'CONTENT',
  VISIBILITY = 'VISIBILITY',
  MONETIZATION = 'MONETIZATION',
  USERS = 'USERS'
}

enum AssignmentTaskSlideBlockParam {
  START_BLOCK_SYNTHETIC_ID = 'START_BLOCK_SYNTHETIC_ID',
  END_BLOCK_SYNTHETIC_ID = 'END_BLOCK_SYNTHETIC_ID'
}

/**
 * Loaders
 */
const LoaderColors = {
  bgColor: '#303030',
  fgColor: '#1d1d1d'
};

/**
 * API REQUESTS -> control which API request to make
 */
enum APIRequestLabel {
  // MASTER
  MASTER_SAY_HELLO = 'MASTER_SAY_HELLO',

  // ACCOUNT
  GET_ACCOUNT_PROFILE = 'GET_ACCOUNT_PROFILE',

  // GOALS
  CREATE_GOAL = 'CREATE_GOAL',
  GET_GOALS = 'GET_GOALS',
  GET_GOALS_LIBRARY = 'GET_GOALS_LIBRARY',
  GET_GOAL = 'GET_GOAL',
  UPDATE_GOAL = 'UPDATE_GOAL',
  PUBLISH_GOAL = 'PUBLISH_GOAL',
  DELETE_GOAL = 'DELETE_GOAL',

  // LIKES
  LIKE = 'LIKE',
  UNLIKE = 'UNLIKE',

  // GOAL SUBSCRIPTIONS
  GET_GOAL_SUBSCRIPTION = 'GET_GOAL_SUBSCRIPTION',
  GET_GOAL_SUBSCRIPTIONS = 'GET_GOAL_SUBSCRIPTIONS',
  CREATE_GOAL_SUBSCRIPTION = 'CREATE_GOAL_SUBSCRIPTION',
  UPDATE_GOAL_SUBSCRIPTION = 'UPDATE_GOAL_SUBSCRIPTION',

  // CHATBOT
  GET_CHATBOT_DATA_ENTRIES = 'GET_CHATBOT_DATA_ENTRIES',
  GET_CHATBOT_DATA_ENTRY = 'GET_CHATBOT_DATA_ENTRY',
  UPDATE_CHATBOT_DATA_ENTRY = 'UPDATE_CHATBOT_DATA_ENTRY',
  CREATE_CHATBOT_DATA_ENTRY = 'CREATE_CHATBOT_DATA_ENTRY',
  DELETE_CHATBOT_DATA_ENTRY = 'DELETE_CHATBOT_DATA_ENTRY',
  GET_CHATBOT_SETTINGS = 'GET_CHATBOT_SETTINGS',
  UPDATE_CHATBOT_SETTINGS = 'UPDATE_CHATBOT_SETTINGS',
  CREATE_CHATBOT_TEST_REQUEST = 'CREATE_CHATBOT_TEST_REQUEST',
  GET_CHATBOT_TEST_REQUEST = 'GET_CHATBOT_TEST_REQUEST',
  UPDATE_CHATBOT_TEST_REQUEST = 'UPDATE_CHATBOT_TEST_REQUEST',
  PUBLISH_CHATBOT = 'PUBLISH_CHATBOT',

  // TASKS
  GET_TASK = 'GET_TASK',
  GET_TASKS = 'GET_TASKS',
  CREATE_TASK = 'CREATE_TASK',
  CLONE_TASK = 'CLONE_TASK',
  UPDATE_TASK = 'UPDATE_TASK',
  DELETE_TASK = 'DELETE_TASK',
  CREATE_TASK_BLOCK = 'CREATE_TASK_BLOCK',
  CLONE_TASK_BLOCK = 'CLONE_TASK_BLOCK',
  UPDATE_TASK_BLOCK = 'UPDATE_TASK_BLOCK',
  DELETE_TASK_BLOCK = 'DELETE_TASK_BLOCK',
  GET_TASK_BLOCK = 'GET_TASK_BLOCK',
  GET_TASK_BLOCK_EXERCISES = 'GET_TASK_BLOCK_EXERCISES',
  GET_TASK_BLOCK_ASSERTION_SPECS = 'GET_TASK_BLOCK_ASSERTION_SPECS',

  // CONVERSATIONS
  GET_CONVERSATION = 'GET_CONVERSATION',
  GET_CONVERSATIONS = 'GET_CONVERSATIONS',
  GET_CONVERSATION_MESSAGE = 'GET_CONVERSATION_MESSAGE',
  GET_CONVERSATION_MESSAGES = 'GET_CONVERSATION_MESSAGES',
  GET_CONVERSATION_MESSAGES_BY_CREATED_AT = 'GET_CONVERSATION_MESSAGES_BY_CREATED_AT',
  CREATE_CONVERSATION_MESSAGE = 'CREATE_CONVERSATION_MESSAGE',
  GET_CONVERSATION_ATTACHMENTS = 'GET_CONVERSATION_ATTACHMENTS',
  MARK_CONVERSATION_MESSAGE_AS_READ = 'MARK_CONVERSATION_MESSAGE_AS_READ',
  MARK_CONVERSATION_MESSAGE_AS_IRRELEVANT = 'MARK_CONVERSATION_MESSAGE_AS_IRRELEVANT',
  MARK_CONVERSATION_MESSAGE_AS_REPLIED = 'MARK_CONVERSATION_MESSAGE_AS_REPLIED',
  MARK_CONVERSATION_MESSAGE_DECLINED_FOR_CHATBOT = 'MARK_CONVERSATION_MESSAGE_AS_DECLINED_FOR_CHATBOT',
  MARK_MESSAGE_RECOMMENDATION_AS_DECLINED = 'MARK_MESSAGE_RECOMMENDATION_AS_DECLINED',
  CREATE_CONVERSATION_PARTICIPANT_PRESENCE = 'CREATE_CONVERSATION_PARTICIPANT_PRESENCE',

  // GARMIN
  GET_GARMIN_CONNECTIONS = 'GET_GARMIN_CONNECTIONS',
  CREATE_GARMIN_CONNECTION = 'CREATE_GARMIN_CONNECTION',
  AUTHORIZE_GARMIN_CONNECTION = 'AUTHORIZE_GARMIN_CONNECTION',

  // SUPPLIES
  GET_SUPPLIES = 'GET_SUPPLIES',

  //TAGS
  GET_TAGS = 'GET_TAGS',
  CREATE_TAG = 'CREATE_TAG',
  GET_TAG = 'GET_TAG',
  UPDATE_TAG = 'UPDATE_TAG',
  DELETE_TAG = 'DELETE_TAG',

  // ACTIVATIONS
  GET_ACTIVATION_PHRASE_TEMPLATES = 'GET_ACTIVATION_PHRASE_TEMPLATES',
  CREATE_ACTIVATION_RULE = 'CREATE_ACTIVATION_RULE',
  GET_ACTIVATION_RULE = 'GET_ACTIVATION_RULE',
  GET_ACTIVATION_RULES = 'GET_ACTIVATION_RULES',
  UPDATE_ACTIVATION_RULE = 'UPDATE_ACTIVATION_RULE',
  DELETE_ACTIVATION_RULE = 'DELETE_ACTIVATION_RULE',

  // STRATEGIES
  GET_STRATEGY = 'GET_STRATEGY',
  PUBLISH_STRATEGY = 'PUBLISH_STRATEGY',
  CREATE_STRATEGY_PATH = 'CREATE_STRATEGY_PATH',
  UPDATE_STRATEGY_PATH = 'UPDATE_STRATEGY_PATH',
  DELETE_STRATEGY_PATH = 'DELETE_STRATEGY_PATH',
  CREATE_STRATEGY_NODES_BATCH = 'CREATE_STRATEGY_NODES_BATCH',
  CREATE_STRATEGY_NODE = 'CREATE_STRATEGY_NODE',
  UPDATE_STRATEGY_NODE = 'UPDATE_STRATEGY_NODE',
  DELETE_STRATEGY_NODE = 'DELETE_STRATEGY_NODE',

  // SCHEDULE
  CREATE_OR_UPDATE_AVAILABILITY = 'CREATE_OR_UPDATE_AVAILABILITY',
  GET_AVAILABILITIES = 'GET_AVAILABILITIES',

  // ASSIGNMENTS
  GET_ASSIGNMENTS = 'GET_ASSIGNMENTS',
  GET_ASSIGNMENT = 'GET_ASSIGNMENT',
  UPDATE_ASSIGNMENT = 'UPDATE_ASSIGNMENT',
  CREATE_BLOCK_OUTPUT_LOGS = 'CREATE_BLOCK_OUTPUT_LOGS',
  GET_BLOCK_OUTPUT_RESULTS = 'GET_BLOCK_OUTPUT_RESULTS',

  // CANCEL_PENDING_SUBSCRIPTION
  CANCEL_PENDING_SUBSCRIPTION = 'CANCEL_PENDING_SUBSCRIPTION'
}

/**
 * GOAL VIEWS -> control the visibility of the actions and other components
 */
enum GoalViewName {
  EXPLORE_ALL = 'explore',
  EXPLORE_MY_SUBSCRIBED = 'explore_my_subscribed',
  EXPLORE_MY_CREATED = 'explore_my_created',
  EXPLORE_MY_LIKED = 'explore_my_liked',
  EXPLORE_MY_COMPLETED = 'explore_my_completed',
  DETAIL = 'detail',
  PROFILE_CREATED = 'profile_created',
  GOALS_LIBRARY = 'goals_library',
  HOME = 'home',
  SUBSCRIPTIONS = 'subscriptions'
}

const GoalComponentsVisibility = {
  [GoalViewName.EXPLORE_ALL]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.EXPLORE_MY_SUBSCRIBED]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.EXPLORE_MY_CREATED]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.PROFILE_CREATED]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.EXPLORE_MY_LIKED]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.EXPLORE_MY_COMPLETED]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.DETAIL]: {
    actions: {
      versionType: true,
      like: true,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.GOALS_LIBRARY]: {
    actions: {
      versionType: true,
      like: false,
      share: false,
      more: true,
      edit: false
    }
  },
  [GoalViewName.HOME]: {
    actions: {
      versionType: false,
      like: false,
      share: true,
      more: false,
      edit: false
    }
  },
  [GoalViewName.SUBSCRIPTIONS]: {
    actions: {
      versionType: false,
      like: false,
      share: true,
      more: false,
      edit: false
    }
  }
};

enum TaskViewName {
  DETAIL = 'detail',
  TASK_LIBRARY = 'task_library',
  STRATEGY_EDITOR = 'strategy_editor'
}

const TaskComponentsVisibility = {
  [TaskViewName.DETAIL]: {
    actions: {
      more: false,
      edit: false
    }
  },
  [TaskViewName.TASK_LIBRARY]: {
    actions: {
      more: true,
      edit: true
    }
  },
  [TaskViewName.STRATEGY_EDITOR]: {
    actions: {
      more: true,
      edit: true
    }
  }
};

/**
 * SCROLLBAR
 */
const DynamicalStyles = {
  scrollbarStyles: (): string => {
    // TODO: somehow this stopped working for chrome browsers.
    // https://github.com/adoxography/tailwind-scrollbar/issues/91
    // A quick global fix was added to the globals.css
    return 'scrollbar-thumb-[#191919] scrollbar-track-black scrollbar-thin';
  }
};

const PORTAL_ROOT_ID = 'portal-root';

/**
 * BLOCK THRESHOLDS
 */
const ValidationConfig = {
  BLOCKS__CHOICES_MAX_COUNT: 10,
  BLOCKS__RATING_MAX_CHARACTERS_IN_VALUE_TEXT: 50,
  BLOCKS__RATING_MIN_MAX_VALUE: 3,
  BLOCKS__RATING_MAX_MAX_VALUE: 9,
  BLOCKS__BRICKS_MAX_CATEGORIES: 4,
  BLOCKS__BRICKS_CATEGORY_NAME_MAX_LENGTH: 30,
  BLOCKS__BRICKS_TEXT_MAX_LENGTH: 500,
  BLOCKS__BRICKS_TEXT_WITH_IMAGE_MAX_LENGTH: 120,
  BLOCKS__BLANKS_MAX_PROBLEMS: 10,
  BLOCKS__BLANKS_MAX_BLANKS_COUNT: 5,
  BLOCKS__BLANKS_TEXT_MAX_LENGTH: 1000,
  BLOCKS__BLANKS_EXPLANATION_MAX_LENGTH: 500,
  BLOCKS__BLANKS_MAX_ATTEMPTS: 3,
  BLOCKS__BLANKS_MIN_SEGMENT_LENGTH: 1,
  BLOCKS__BLANKS_GAME_CONTEXT_LIMIT: 80,
  BLOCKS__INPUT_TEXT_MAX_LENGTH: 2000,
  MESSAGE__CONTENT__MAX_LENGTH: 10000,
  CHATBOT_ENTRY__QUESTION__MAX_LENGTH: 1000,
  CHATBOT_ENTRY__ANSWER__MAX_LENGTH: 10000,
  TEMPLATE_MESSAGE__MAX_LENGTH: 1000,
  GOAL__TITLE__MAX_LENGTH: 150,
  GOAL__SHORT_DESCRIPTION__MAX_LENGTH: 300,
  GOAL__DESCRIPTION__MAX_LENGTH: 5000,
  TASK__TITLE__MAX_LENGTH: 200,
  DICTATION__MAX_RECORDING_SECONDS: 300
};

enum KeyEvent {
  ENTER = 'Enter',
  ESCAPE = 'Escape'
}

// In next iteration we will use the thresholds from the backend
const BlockInputFeedbackLevels = {
  high: 0.8,
  medium: 0.5,
  low: 0.2
};

enum WebViewEvents {
  LOGOUT = 'logout',
  REFRESH_TOKEN = 'refresh_token'
}

const SHAKE_ANIMATION = {
  x: [0, -10, 10, -10, 10, -5, 5, -5, 5, 0],
  transition: { duration: 0.5 }
};

const SHAKE_ANIMATION_DELAY = 400; // ms - delay before starting shake animation
const SHAKE_ANIMATION_RESET_DELAY = 800; // ms - delay before resetting shake animation

// SCORE BAR ANIMATION
const SCORE_BAR_ANIMATION_STEP = 0.01;
const SCORE_BAR_ANIMATION_INTERVAL_TIME = 24; // ms

// GAME INPUT TYPES
enum GameType {
  BRICKS = 'BRICKS',
  BLANKS = 'BLANKS',
  FLASHCARDS = 'FLASHCARDS'
}

enum BlankSegmentType {
  TEXT = 'text',
  BLANK = 'blank'
}

const DEFAULT_LANDING_PAGE_ACCENT_COLOR = '#ff4187';

const DISCORD_INVITE_URL = 'https://discord.gg/mtzpqHweWY';

const DISCORD_BRAND_COLOR = '#5865F2';

const GoalUsersFilterThresholds = {
  BARELY_STARTED_MAX_PROGRESS: 0.03, // below 3% of total
  HARD_MATERIAL_MIN_PROGRESS: 0.1, // above 10% of total
  HARD_MATERIAL_LOW_RATIO: 0.4, // 40% of completed is LOW
  BEST_TOP_COUNT: 20, // floor: always show at least 20 best users when available
  BEST_TOP_PERCENT: 0.15 // alternatively show top 15% of total, whichever is larger
};

const GOAL_RECOMMENDATION_COUNT_HIDDEN_UNTIL_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const MILLISECONDS_WE_CONSIDER_GOAL_NEW = 30 * 24 * 60 * 60 * 1000; // 30 days

// PAYMENTS — thank-you page polling
const PAYMENT_THANK_YOU_POLL_MAX_RETRIES = 10;
const PAYMENT_THANK_YOU_POLL_INTERVAL_MS = 6000;

// The HTTP layer sends no timeout, so without a cap a stalled lookup traps a buyer who already paid.
const PAYMENT_LINK_PRODUCT_LOOKUP_TIMEOUT_MS = 5000;

// A Pusher push can be lost, so a card with no push in time is read by polling.
const FLASHCARD_GRADE_FIRST_POLL_DELAY_MS = 10 * 1000;
const FLASHCARD_GRADE_POLL_INTERVAL_MS = 5 * 1000;
const FLASHCARD_GRADE_TIMEOUT_MS = 60 * 1000;
// After the time-out the wait has no end, and every read loads the whole assignment,
// so the poll slows down.
const FLASHCARD_GRADE_SLOW_POLL_INTERVAL_MS = 15 * 1000;

export {
  AccountsMeFieldsUsage,
  ALLOWED_FILE_UPLOAD_CONTENT_TYPED,
  API_ENDPOINTS,
  APIRequestLabel,
  AssignmentsTabs,
  AssignmentTaskSlideBlockParam,
  AUTH_ERROR_MESSAGE,
  AUTH_ERROR_TYPE,
  AUTH_SESSION_DEATH_ERROR_CODES,
  AuthSessionDeathErrorCode,
  BlankSegmentType,
  BLOCK_EVALUATION_CHARGE_EVENTS,
  BLOCK_OUTPUT_FLUSH_INTERVAL_MS,
  BLOCK_OUTPUT_FLUSH_THRESHOLD,
  BlockInputFeedbackLevels,
  BREAKPOINTS,
  CONVERSATION_CHARGE_EVENTS,
  CONVERSATION_CHATBOT_ACK_TIMEOUT_MS,
  CONVERSATION_MESSAGE_QUEUE_IDLE_MS,
  CONVERSATION_MESSAGES_DEFAULT_SIZE,
  CONVERSATION_MESSAGES_FETCH_BATCH_SIZE,
  CONVERSATION_MESSAGES_FIRST_INDEX_BASE,
  CONVERSATION_MESSAGES_INCREASE_VIEWPORT_BY_BOTTOM,
  CONVERSATION_MESSAGES_INCREASE_VIEWPORT_BY_TOP,
  ConversationErrorCode,
  DEFAULT_CURRENCY,
  DEFAULT_LANDING_PAGE_ACCENT_COLOR,
  DEFAULT_LANGUAGE,
  DESKTOP_MAX_WIDTH,
  DISCORD_BRAND_COLOR,
  DISCORD_INVITE_URL,
  DynamicalStyles,
  FLASHCARD_GRADE_FIRST_POLL_DELAY_MS,
  FLASHCARD_GRADE_POLL_INTERVAL_MS,
  FLASHCARD_GRADE_SLOW_POLL_INTERVAL_MS,
  FLASHCARD_GRADE_TIMEOUT_MS,
  FORCE_ALLOW_CHAT_FOR_GOAL_ENTITY_IDS,
  GameType,
  GOAL_RECOMMENDATION_COUNT_HIDDEN_UNTIL_DURATION,
  GoalComponentsVisibility,
  GoalManagementTab,
  GoalUsersFilterThresholds,
  GoalUserTabs,
  GoalViewName,
  IMPROVER_ATTACHMENT_MIME_TYPE_TO_EXTENSION,
  IMPRV_COINS_BALANCE_REFRESH_DEBOUNCE_MS,
  IMPRV_COINS_EXPIRY_WARNING_DAYS,
  IMPRV_COINS_LOW_BALANCE_THRESHOLD,
  KeyEvent,
  LoaderColors,
  LocalStorageKeys,
  MAX_ATTACHMENT_FILE_SIZE,
  MENTOR_USER_IDS_WITH_DISABLED_CHAT,
  MENTORBOT_THINKING_TIMEOUT_MS,
  MILLISECONDS_WE_CONSIDER_GOAL_NEW,
  NAVIGATION_CONTEXT,
  Page,
  PAYMENT_LINK_PRODUCT_LOOKUP_TIMEOUT_MS,
  PAYMENT_THANK_YOU_POLL_INTERVAL_MS,
  PAYMENT_THANK_YOU_POLL_MAX_RETRIES,
  PaymentsErrorCode,
  PORTAL_ROOT_ID,
  REORDERABLE_LIST_COLLAPSED_SLOT_HEIGHT_PX,
  REORDERABLE_LIST_FALLBACK_DRAG_ROW_HEIGHT_PX,
  REORDERABLE_LIST_FALLBACK_DRAG_ROW_WIDTH_PX,
  ROUTER_NAME,
  SCORE_BAR_ANIMATION_INTERVAL_TIME,
  SCORE_BAR_ANIMATION_STEP,
  SessionStorageKeys,
  SHAKE_ANIMATION,
  SHAKE_ANIMATION_DELAY,
  SHAKE_ANIMATION_RESET_DELAY,
  SIZING,
  TASK_ENTRY_SOURCE,
  TaskComponentsVisibility,
  TaskViewName,
  TRANSCRIPT_PENDING_MAX_AGE_MS,
  UIPaths,
  UPLOAD_ARCHIVE_ACCEPT_CONTENT_TYPES,
  UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES,
  UPLOAD_AUDIO_MAX_SIZE,
  UPLOAD_DOCUMENT_ACCEPT_CONTENT_TYPES,
  UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES,
  UPLOAD_IMAGE_MAX_SIZE,
  UPLOAD_MEDIA_ONLY_ACCEPT_CONTENT_TYPES,
  UPLOAD_MEDIAITEM_ACCEPT_CONTENT_TYPES,
  UPLOAD_MEDIAITEM_MAX_SIZE,
  UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES,
  UPLOAD_VIDEO_MAX_SIZE,
  ValidationConfig,
  VIDEO_HEARTBEAT_INTERVAL_MS,
  WebViewEvents
};
